/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';
import { QueryClientProvider, QueryClient } from '@tanstack/react-query';

import { LaunchDarklyProvider } from 'sarsaparilla';

import { AuthModalProvider } from 'shared-ui/auth';
import { RecaptchaProvider } from 'shared-ui/recaptcha';

import { GlobalDialogs, type GlobalDialogsProps } from './GlobalDialogs';

const defaultQueryClient = new QueryClient();

interface ExternalAppProvidersProps extends GlobalDialogsProps {
    children: React.ReactNode;
    launchDarklyWaitUntilReady?: boolean;
    queryClient?: QueryClient;
}

/**
 * Behavior-only provider stack required by external apps (LaunchDarkly,
 * React Query, Recaptcha, and Auth). Renders no DOM elements.
 *
 * Must be mounted under a Redux `Provider` (Auth depends on it). Pass a stable
 * `queryClient` when route loaders prefetch into a shared cache; otherwise a
 * module-level default client is used.
 *
 * It also mounts `GlobalDialogs`, the host for the platform's app-wide overlays,
 * because this stack is the one thing every external app already mounts. That keeps
 * the no-DOM contract: every dialog admitted to the host portals to `document.body`
 * and renders nothing until something raises it, so nothing is added to this subtree
 * and apps keep sole ownership of their layout. `GlobalDialogs` states the rule for
 * what may live there; its props are forwarded from here.
 */
export function ExternalAppProviders({
    children,
    launchDarklyWaitUntilReady = true,
    queryClient,
    // Forwarded whole, so admitting a second dialog to the host is one file's change
    ...globalDialogs
}: ExternalAppProvidersProps) {
    return (
        <LaunchDarklyProvider waitUntilReady={launchDarklyWaitUntilReady}>
            <QueryClientProvider client={queryClient ?? defaultQueryClient}>
                <RecaptchaProvider>
                    <AuthModalProvider>
                        {children}
                        <GlobalDialogs {...globalDialogs} />
                    </AuthModalProvider>
                </RecaptchaProvider>
            </QueryClientProvider>
        </LaunchDarklyProvider>
    );
}
